import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { getAuthClient } from './authClient';
import { AUTH_ENABLED } from './flags';
import { getSubscriptionStatus } from '../features/subscription/services/subscriptionApi';
import { login } from '../features/auth/services/authApi';
import { updatePreferences as updatePreferencesApi } from '../features/preferences/services/preferencesApi';
import { clearUserData } from '../lib/http';
import { logger } from '../lib/logger';

const Ctx = createContext(null);
export const useAuth = () => useContext(Ctx);

// Default preferences
const DEFAULT_PREFERENCES = {
    dashboardLayout: 'CARD',
    theme: 'DARK',
    country: '',
    defaultDifficulty: 'BEGINNER',
};

export default function AuthProvider({ children }) {
    const auth = getAuthClient();
    const [user, setUser] = useState(null);
    const [ready, setReady] = useState(false);
    const [subscription, setSubscription] = useState(null);
    const [subscriptionLoading, setSubscriptionLoading] = useState(false);
    const [preferences, setPreferences] = useState(DEFAULT_PREFERENCES);
    const [preferencesLoading, setPreferencesLoading] = useState(false);

    // Listen for auth state changes
    useEffect(() => {
        const unsub = auth.onChange((u) => {
            setUser(u);
            setReady(true);
        });
        return () => {
            try { unsub && unsub(); } catch {}
        };
        // `auth` is a stable module-level singleton; this subscription
        // should run exactly once on mount.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Call /auth/login when user authenticates to sync with backend and get preferences
    useEffect(() => {
        if (!AUTH_ENABLED || !ready || !user) {
            if (!user) {
                setPreferences(DEFAULT_PREFERENCES);
            }
            return;
        }

        const email = user.email || '';
        const displayName = user.displayName || '';

        (async () => {
            try {
                logger.log('[auth-login] calling /api/v1/auth/login', { email, displayName });
                const res = await login({ email, displayName, auth: true });
                logger.log('[auth-login] success', res);

                // Capture preferences from login response
                if (res?.preferences) {
                    logger.log('[auth-login] preferences loaded:', res.preferences);
                    setPreferences({
                        dashboardLayout: res.preferences.dashboardLayout || DEFAULT_PREFERENCES.dashboardLayout,
                        theme: res.preferences.theme || DEFAULT_PREFERENCES.theme,
                        country: res.preferences.country || DEFAULT_PREFERENCES.country,
                        defaultDifficulty: res.preferences.defaultDifficulty || DEFAULT_PREFERENCES.defaultDifficulty,
                    });
                }
            } catch (e) {
                logger.warn('[auth-login] failed', e);
            }
        })();
    }, [ready, user]);

    // Fetch subscription status when user is authenticated
    useEffect(() => {
        if (!ready || !user) {
            setSubscription(null);
            return;
        }

        const fetchSubscription = async () => {
            setSubscriptionLoading(true);
            try {
                const status = await getSubscriptionStatus();
                logger.log('[subscription] Fetched status:', status);
                setSubscription(status);
            } catch (e) {
                console.error('[subscription] Failed to fetch status:', e);
                setSubscription(null);
            } finally {
                setSubscriptionLoading(false);
            }
        };

        fetchSubscription();
    }, [ready, user]);

    // Helper to refresh subscription (call after checkout completes)
    const refreshSubscription = async () => {
        if (!user) return;
        setSubscriptionLoading(true);
        try {
            const status = await getSubscriptionStatus();
            logger.log('[subscription] Refreshed status:', status);
            setSubscription(status);
        } catch (e) {
            console.error('[subscription] Failed to refresh status:', e);
        } finally {
            setSubscriptionLoading(false);
        }
    };

    // Update preferences on the backend and locally
    const updatePreferences = useCallback(async (newPreferences) => {
        if (!user?.uid) {
            logger.warn('[preferences] Cannot update: no user');
            return;
        }

        setPreferencesLoading(true);
        try {
            // Merge with current preferences
            const merged = { ...preferences, ...newPreferences };

            logger.log('[preferences] Updating:', merged);
            const res = await updatePreferencesApi({
                userId: user.uid,
                dashboardLayout: merged.dashboardLayout,
                theme: merged.theme,
                country: merged.country,
                preferredDifficulty: merged.defaultDifficulty,
            });

            logger.log('[preferences] Update response:', res);

            // Update local state with response
            if (res?.preferences) {
                setPreferences({
                    dashboardLayout: res.preferences.dashboardLayout || merged.dashboardLayout,
                    theme: res.preferences.theme || merged.theme,
                    country: res.preferences.country || merged.country,
                    defaultDifficulty: res.preferences.defaultDifficulty || merged.defaultDifficulty,
                });
            } else {
                // Fallback to local merge if response doesn't include preferences
                setPreferences(merged);
            }

            return res;
        } catch (e) {
            console.error('[preferences] Failed to update:', e);
            throw e;
        } finally {
            setPreferencesLoading(false);
        }
    }, [user, preferences]);

    // Wrap logout to clear user data and reset state
    const logout = useCallback(async () => {
        clearUserData();
        setSubscription(null);
        setPreferences(DEFAULT_PREFERENCES);
        await auth.signOut();
        // `auth` is a stable module-level singleton; this callback should
        // keep a stable identity across renders.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const value = {
        // Auth
        user,
        ready,
        mode: auth.mode,
        signInGoogle: () => auth.signInWithGoogle(),
        signInApple: () => auth.signInWithApple(),
        logout,
        getIdToken: () => auth.getIdToken(),
        enabled: AUTH_ENABLED,

        // Subscription
        subscription,
        subscriptionLoading,
        refreshSubscription,
        setSubscription, // Allow components to update subscription after polling
        isSubscribed: subscription?.active ?? false,

        // Preferences
        preferences,
        preferencesLoading,
        updatePreferences,
    };

    return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
